import React, { useEffect, useState } from "react";
import { Combine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export default function MergeDialog({ target, candidates, lots, onClose, onMerged }) {
  const [sourceId, setSourceId] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!target) {
      setSourceId("");
      setConfirmText("");
    }
  }, [target]);

  if (!target) return null;

  const eligibleSources = (candidates || []).filter((c) => c.id !== target.id && c.type === target.type);
  const source = eligibleSources.find((c) => c.id === sourceId);

  const targetEmps = (target.emplacements || []).length;
  const sourceEmps = (source?.emplacements || []).length;
  const sourceBarq = lots.filter((l) => l.distributor_id === source?.id && (l.quantity || 0) > 0).length;

  const expected = `FUSIONNER ${target.name}`.toUpperCase();
  const canSubmit = !!source && confirmText.trim().toUpperCase() === expected && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/distributors/${target.id}/merge`, { source_id: source.id });
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de fusion");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Combine className="w-5 h-5 text-[#1F4E3D]" /> Fusionner deux dépôts
          </DialogTitle>
          <DialogDescription>
            Sélectionnez le dépôt source à fusionner dans <strong>{target.name}</strong>. Tous les lots, mouvements,
            ventes et inventaires du source seront migrés vers la cible. Le dépôt source sera{" "}
            <strong>supprimé définitivement</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="mb-1.5 block">Dépôt à fusionner (source)</Label>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger data-testid="merge-source-select">
                <SelectValue placeholder="Choisir un dépôt..." />
              </SelectTrigger>
              <SelectContent>
                {eligibleSources.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[#A3A6A3]">
                    Aucun dépôt compatible (type {target.type})
                  </div>
                ) : (
                  eligibleSources.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{" "}
                      {c.code && <span className="font-mono text-xs text-[#575A57]">· {c.code}</span>}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {source && (
            <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded-md p-3 text-sm space-y-2">
              <div className="font-semibold text-[#1A1C1A]">Aperçu de la fusion</div>
              <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                <div>
                  <div className="text-[#575A57] uppercase tracking-wider text-[10px] mb-0.5">Cible</div>
                  <div className="text-[#1A1C1A]">{target.name}</div>
                  <div className="text-[#575A57]">{targetEmps} empl.</div>
                </div>
                <div>
                  <div className="text-[#575A57] uppercase tracking-wider text-[10px] mb-0.5">
                    Source (sera supprimée)
                  </div>
                  <div className="text-[#1A1C1A]">{source.name}</div>
                  <div className="text-[#575A57]">
                    {sourceEmps} empl. · {sourceBarq} barq.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-[#3A3A3A] pt-1">
                <Combine className="w-3.5 h-3.5 mt-0.5 text-[#1F4E3D] shrink-0" />
                <div>
                  Le dépôt fusionné aura{" "}
                  <strong className="font-mono">≥ {targetEmps + sourceEmps}</strong> emplacements (les
                  emplacements en conflit seront renumérotés).
                </div>
              </div>
            </div>
          )}

          <div className="bg-[#FCEEEA] border border-[#C05C47]/30 rounded-md p-3 text-xs flex gap-2 items-start">
            <AlertTriangle className="w-4 h-4 text-[#C05C47] shrink-0 mt-0.5" />
            <div className="text-[#8B3D2D]">
              <strong>Action irréversible.</strong> Pensez à exporter vos données depuis Paramètres → Sauvegarde
              avant de fusionner.
            </div>
          </div>

          {source && (
            <div>
              <Label className="mb-1.5 block">
                Pour confirmer, tapez : <span className="font-mono text-[#1F4E3D]">{expected}</span>
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expected}
                className="font-mono"
                data-testid="merge-confirm-text"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-[#C05C47] hover:bg-[#A64A37] text-white"
            data-testid="merge-submit-btn"
          >
            {submitting ? "Fusion en cours…" : "Fusionner et supprimer la source"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
